import { useMemo } from 'react'
import type {
  CleaningJob,
  PropertyBlocks,
  PropertyCalendar,
  PropertyCalendarBlock,
  PropertyCalendarDeposit,
  PropertyCalendarOrphanGap,
  PropertyCalendarReview,
  PropertyCalendarStay,
  PropertyCalendarSupply,
} from '@/api/client'
import { blockKey, type CalendarBlockLike } from '@/components/calendar/block-bars'
import { DEFAULT_PROPERTY_TIME_ZONE } from '@/components/cleaning/presentation'
import { buildDepositMarkers, groupMarkersByDay, type DepositMarker } from '@/lib/deposit-events'
import {
  buildReviewMarkers,
  groupReviewMarkersByDay,
  reviewToday,
  type ReviewMarker,
} from '@/lib/review-events'
import { buildSupplyMarkers, groupSupplyMarkersByDay, type SupplyMarker } from '@/lib/supply-events'
import { nextMonthStart } from './agenda'
import type { CalendarLayerId } from './layers'

/**
 * The days a calendar read covers, as property-local `YYYY-MM-DD`: `start`
 * included, `end` not. The property tab's is the month on screen; the
 * portfolio calendar's is every window it has read for the property.
 */
export interface CalendarRange {
  start: string
  end: string
}

export function monthRange(month: string): CalendarRange {
  return { start: `${month}-01`, end: nextMonthStart(month) }
}

export function rangeHolds(range: CalendarRange, day: string): boolean {
  return day >= range.start && day < range.end
}

/**
 * Everything a property's calendar read holds, as the grid draws it and the
 * sheets look things up in it: shared by the property Calendar tab and the
 * portfolio calendar's sheets (CAP-1418), so both read one read the same way.
 */
export interface CalendarData {
  timeZone: string
  /** The server's day, for whether a review window is still open. */
  today: string | null
  stays: PropertyCalendarStay[]
  cleaningJobs: CleaningJob[]
  depositItems: PropertyCalendarDeposit[]
  /** The blocks drawn: the read's, plus any the blocks read has that it does not carry yet. */
  blockItems: PropertyCalendarBlock[]
  reviewItems: PropertyCalendarReview[]
  supplyItems: PropertyCalendarSupply[]
  /** The gaps with a night in the range. */
  gapItems: PropertyCalendarOrphanGap[]
  /** The range's markers by day, for the layers switched on (empty for one switched off). */
  markersByDay: Map<string, DepositMarker[]>
  supplyMarkersByDay: Map<string, SupplyMarker[]>
  reviewMarkersByDay: Map<string, ReviewMarker[]>
  guestNames: Map<string, string | null>
  /** Every block and hold known, in any month, to say what a block overlaps. */
  blockPool: CalendarBlockLike[]
}

interface UseCalendarDataInput {
  calendar: PropertyCalendar | undefined
  range: CalendarRange
  /** The blocks read (`usePropertyBlocks`), when it has answered. */
  blocksData: PropertyBlocks | undefined
  isVisible: (id: CalendarLayerId) => boolean
}

export function useCalendarData({
  calendar,
  range,
  blocksData,
  isVisible,
}: UseCalendarDataInput): CalendarData {
  const { start: rangeStart, end: rangeEnd } = range
  const stays = useMemo<PropertyCalendarStay[]>(
    () => (calendar?.layers.stays.status === 'ok' ? calendar.layers.stays.items : []),
    [calendar]
  )
  const showDeposits = isVisible('deposits')
  const showSupplies = isVisible('supplies')
  const showReviews = isVisible('reviews')

  const cleaningJobs = useMemo<CleaningJob[]>(
    () => (calendar?.layers.cleaning.status === 'ok' ? calendar.layers.cleaning.items : []),
    [calendar]
  )
  const depositItems = useMemo<PropertyCalendarDeposit[]>(
    () => (calendar?.layers.deposits.status === 'ok' ? calendar.layers.deposits.items : []),
    [calendar]
  )
  const blockItems = useMemo<PropertyCalendarBlock[]>(() => {
    if (calendar?.layers.blocks.status !== 'ok') return []
    const items = calendar.layers.blocks.items
    if (!blocksData) return items
    // A block the blocks read has and the month read does not (made a moment
    // ago, before the month read caught up) is drawn too, so a block you just
    // made does not vanish for a refresh.
    const known = new Set(items.map(item => blockKey(item)))
    const extra: PropertyCalendarBlock[] = []
    for (const block of blocksData.owner_blocks) {
      if (!block.start || !block.end || known.has(`owner:${block.block_id}`)) continue
      extra.push({
        id: block.block_id,
        kind: 'owner',
        start: block.start,
        end_exclusive: block.end,
        note: block.note ?? null,
        sync_status: block.sync_status ?? null,
      })
    }
    for (const hold of blocksData.external_blocks) {
      if (!hold.start || !hold.end || known.has(`uplisting:${hold.id}`)) continue
      extra.push({
        id: hold.id,
        kind: 'uplisting',
        start: hold.start,
        end_exclusive: hold.end,
        note: null,
        sync_status: null,
      })
    }
    return [
      ...items,
      ...extra.filter(block => block.start < rangeEnd && block.end_exclusive > rangeStart),
    ]
  }, [calendar, blocksData, rangeStart, rangeEnd])
  const reviewItems = useMemo<PropertyCalendarReview[]>(
    () => (calendar?.layers.reviews.status === 'ok' ? calendar.layers.reviews.items : []),
    [calendar]
  )
  const supplyItems = useMemo<PropertyCalendarSupply[]>(
    () => (calendar?.layers.supplies.status === 'ok' ? calendar.layers.supplies.items : []),
    [calendar]
  )
  // The gaps of the range on screen. The read returns a gap whole, so one that
  // crosses the range's edge is in both ranges' reads; the grid clips it to the
  // range it draws.
  const gapItems = useMemo<PropertyCalendarOrphanGap[]>(
    () =>
      calendar?.layers.orphan_gaps.status === 'ok'
        ? calendar.layers.orphan_gaps.items.filter(
            gap => gap.last_night >= rangeStart && gap.first_night < rangeEnd
          )
        : [],
    [calendar, rangeStart, rangeEnd]
  )
  const timeZone = calendar?.time_zone ?? DEFAULT_PROPERTY_TIME_ZONE
  const inRange = (day: string) => day >= rangeStart && day < rangeEnd
  // Only the range's markers: a deposit carries its whole timeline.
  const markersByDay = useMemo(
    () =>
      showDeposits
        ? groupMarkersByDay(buildDepositMarkers(depositItems, timeZone, inRange))
        : new Map<string, DepositMarker[]>(),
    // `inRange` is the range's bounds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showDeposits, depositItems, timeZone, rangeStart, rangeEnd]
  )
  const supplyMarkersByDay = useMemo(
    () =>
      showSupplies
        ? groupSupplyMarkersByDay(buildSupplyMarkers(supplyItems, inRange))
        : new Map<string, SupplyMarker[]>(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showSupplies, supplyItems, rangeStart, rangeEnd]
  )
  // The server's day, for whether a review window is still open.
  const today = reviewToday(calendar?.generated_at, timeZone)
  const guestNames = useMemo(
    () => new Map(stays.map(stay => [stay.reservation_id, stay.guest_name])),
    [stays]
  )
  const reviewMarkersByDay = useMemo(
    () =>
      showReviews
        ? groupReviewMarkersByDay(
            buildReviewMarkers(reviewItems, { today, guestNames, inDay: inRange })
          )
        : new Map<string, ReviewMarker[]>(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showReviews, reviewItems, today, guestNames, rangeStart, rangeEnd]
  )

  // Every block and hold the page knows of, in any month, to say what a block
  // overlaps: the month read's, plus the blocks read's (which spans all months).
  const blockPool = useMemo<CalendarBlockLike[]>(() => {
    const pool = new Map<string, CalendarBlockLike>()
    for (const block of blockItems) pool.set(blockKey(block), block)
    // The blocks read is the fresher of the two right after a create or a
    // removal, so where both have a block, it wins.
    for (const block of blocksData?.owner_blocks ?? []) {
      if (!block.start || !block.end) continue
      pool.set(`owner:${block.block_id}`, {
        id: block.block_id,
        kind: 'owner',
        start: block.start,
        end_exclusive: block.end,
        note: block.note ?? null,
        sync_status: block.sync_status ?? null,
      })
    }
    for (const hold of blocksData?.external_blocks ?? []) {
      if (!hold.start || !hold.end) continue
      pool.set(`uplisting:${hold.id}`, {
        id: hold.id,
        kind: 'uplisting',
        start: hold.start,
        end_exclusive: hold.end,
        note: null,
        sync_status: null,
      })
    }
    return [...pool.values()]
  }, [blockItems, blocksData])

  return {
    timeZone,
    today,
    stays,
    cleaningJobs,
    depositItems,
    blockItems,
    reviewItems,
    supplyItems,
    gapItems,
    markersByDay,
    supplyMarkersByDay,
    reviewMarkersByDay,
    guestNames,
    blockPool,
  }
}
